import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Popover, MenuItem, FormControlLabel, Checkbox, Button, Typography, InputBase } from '@mui/material';
import { ReactComponent as FilterIcon } from '../../assets/ml_summary_filter.svg';
import { getDecodedToken, fetchAzureSectors, getPepwiseSectorList } from '../../service/service';
import { useTheme } from "@mui/material/styles";
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import { ReactComponent as FilterIconNew } from '../../assets/PepWise2.0/Filter - Icon - New.svg';

type SectorCheckFilterProps = {
    sectorStorageName: string;
    handleOkClick: () => void; 
    checkedListRef: React.MutableRefObject<any>;
    azureStorage?: boolean;
    disable?:boolean
    isNew?:boolean
};

type CheckboxIconProps = {
    borderColor?: string;
    checkColor?: string;
    backgroundColor?: string;
};

const UncheckedIcon = ({
    borderColor = '#AAAAAA',
    backgroundColor = '#fff',
}: CheckboxIconProps) => (
    <Box
        sx={{
            width: 18,
            height: 18,
            borderRadius: '4px',
            border: `1px solid ${borderColor}`,
            backgroundColor,
            boxSizing: 'border-box',
        }}
    />
);

const CheckedIconBox = ({
    borderColor = '#39ACE7',
    checkColor = '#39ACE7',
    backgroundColor = '#fff',
}: CheckboxIconProps) => (
    <Box
        sx={{
            width: 18,
            height: 18,
            borderRadius: '4px',
            border: `1px solid ${borderColor}`,
            backgroundColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
        }}
    >
        <CheckIcon
            sx={{
                '&&': {
                    fontSize: 14,
                    color: `${checkColor} !important`,
                    fill: `${checkColor} !important`,
                },
            }}
        />
    </Box>
);

const SectorCheckFilter = ({ sectorStorageName, handleOkClick, checkedListRef,azureStorage, disable = false, isNew = false }: SectorCheckFilterProps) => {
    const theme = useTheme();
    const colors = theme.palette;
    const dropdownColors = colors.filterDropdown;
    const userSector = getDecodedToken()?.sector?.name?.toLowerCase();
    const [filterMenu, setFilterMenu] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);
    const [checkedList, setCheckedList] = useState<any[]>([]);
    const [itemsChecked, setItemsChecked] = useState(false);
    const [sectorOptionsList, setSectorOptionsList] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 200);
        return () => clearTimeout(timer);
    }, [search]);

    const visibleSectorOptions = useMemo(() => {
        if (!isNew || !debouncedSearch.trim()) return sectorOptionsList;
        const searchValue = debouncedSearch.trim().toLowerCase();
        return sectorOptionsList.filter((sector: any) => String(sector).toLowerCase().includes(searchValue));
    }, [sectorOptionsList, debouncedSearch, isNew]);

    // Common styles
    const commonMenuItemStyles = {
        margin: 0,
        padding: 0,
        '& .MuiFormControlLabel-root': {
            margin: 0,
            width: '100%',
			gap: '8px',
        },
        '& .MuiCheckbox-root': {
            padding: 0
        },

        backgroundColor: `${isNew ? dropdownColors.panelBackground : colors.dropdown.menuItemBackground} !important`,
        color: `${isNew ? dropdownColors.optionText : colors.dropdown.menuItemText} !important`,
        '&.Mui-selected': {
            backgroundColor: `${isNew ? dropdownColors.panelBackground : colors.dropdown.menuItemSelected} !important`,
            color: `${isNew ? dropdownColors.optionText : colors.dropdown.menuItemHoverText} !important`,
        },
        '&.Mui-selected:hover': {
            backgroundColor: `${isNew ? dropdownColors.optionHoverBackground : colors.dropdown.menuItemHover} !important`,
            color: `${isNew ? dropdownColors.optionText : colors.dropdown.menuItemHoverText} !important`,
        },
        '&:hover': {
            backgroundColor: `${isNew ? dropdownColors.optionHoverBackground : colors.dropdown.menuItemHover} !important`,
            color: `${isNew ? dropdownColors.optionText : colors.dropdown.menuItemHoverText} !important`,
        },
    };

    async function fetchSectorOptionsData() {
        if(azureStorage){
            const sectorRes = await fetchAzureSectors();
            if (sectorRes.success) {
                setSectorOptionsList(sectorRes.data.data?.filter((p:string) => p !== 'Unknown'));
            }
        } else {
            const sectorRes = await getPepwiseSectorList();
            if (sectorRes.success) {
                const sectorNames = sectorRes.data.sectors
                    ?.map((sector: any) => sector.Sector_Name)
                    ?.filter((name: string) => name !== 'Unknown') || [];
                setSectorOptionsList(sectorNames);
            }
        }
    }

    useEffect(() => {
        if (userSector === 'all') {
            fetchSectorOptionsData();
        }
    }, []);

    const filterClick = (event: any) => {
        event.preventDefault();
        setFilterMenu(filterMenu === null ? { mouseX: event.clientX - 20, mouseY: event.clientY - 6 } : null);
    };

    const handleClose = () => {
        setFilterMenu(null);
        if (isNew) {
            setSearch('');
            setDebouncedSearch('');
        }
        let storedSectorFilter =
            !!sectorStorageName && localStorage.getItem(sectorStorageName)
                ? JSON.parse(localStorage.getItem(sectorStorageName) ?? '')
                : null;
        if (storedSectorFilter) {
            setCheckedList(storedSectorFilter);
            checkedListRef.current = storedSectorFilter;
        } else {
            setCheckedList([]);
            checkedListRef.current = [];
        }
        if (storedSectorFilter && storedSectorFilter.length == sectorOptionsList.length) setItemsChecked(true);
        else setItemsChecked(false);
    };

    const selectAllCheck = (e: any) => {
        const { checked } = e.target;
        if (checked) {
            setCheckedList(sectorOptionsList);
            checkedListRef.current = sectorOptionsList;
        } else {
            setCheckedList([]);
            checkedListRef.current = [];
        }
        setItemsChecked(checked);
    };

    const handleOkChange = (event: any) => {
        if (sectorStorageName) {
            localStorage.setItem(sectorStorageName, JSON.stringify(checkedList));
        }
        handleOkClick();
        setFilterMenu(null);
        if (isNew) {
            setSearch('');
            setDebouncedSearch('');
        }
    };

    const handleChange = (e: any) => {
        const { value, checked } = e.target;
        const newlyChecked = [];
        if (checked && e.target.value) {
            newlyChecked.push(e.target.value);
        }
        if (checked) {
            setCheckedList([...checkedList, ...newlyChecked]);
            checkedListRef.current = [...checkedList, ...newlyChecked];
        } else {
            const checkedValue = checkedList.filter((item) => item !== value);
            setCheckedList(checkedValue);
            checkedListRef.current = checkedValue;
            setItemsChecked(checked);
        }
        if (sectorOptionsList.length === checkedList.length + 1) {
            setItemsChecked(true);
        }
    };

    return (
        <Stack sx={{...(disable && { pointerEvents: 'none', opacity: 0.5 })}}>
            <Stack
                direction="row"
                alignItems="center"
                sx={{ backgroundColor: isNew ? 'transparent' : 'primary.main', width: 'auto', padding: 0, borderRadius: '5px', ...(isNew && { gap: 0.5 }) }}
            >
                {userSector === 'all' ? (
                    <>
                        <Typography variant="filterText" onClick={filterClick} style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} sx={{ lineHeight: '30px', px: isNew ? 0 : 2, maxWidth: 100, ...(isNew && { color: `${dropdownColors.fieldIcon} !important`, fontWeight: 500 }) }}>
                            {checkedListRef.current.length === sectorOptionsList.length || checkedListRef.current.length === 0
                            ? 'All Sectors'
                            : checkedListRef.current.length === 1
                            ? checkedListRef.current[0]
                            : `${checkedListRef.current.length} Sectors`}
                            </Typography>
                        {isNew ? (
                            <FilterIconNew onClick={filterClick} style={{ cursor: 'pointer', gap: 1 }} />
                        ) : (
                            <FilterIcon onClick={filterClick} style={{ cursor: 'pointer' }} />
                        )}
                    </>
                ) : (
                    <Typography variant="filterText" sx={{ lineHeight: '30px', px: isNew ? 0 : 2, ...(isNew && { color: `${dropdownColors.fieldIcon} !important`, fontWeight: 500 }) }}>Sector: {getDecodedToken()?.sector?.name}</Typography>
                )}
            </Stack>
            <Popover
                open={filterMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={filterMenu ? { top: filterMenu.mouseY, left: filterMenu.mouseX } : undefined}
            >
                <Stack spacing={1} sx={{ backgroundColor: `${isNew ? dropdownColors.panelBackground : colors.dropdown.menuItemBackground} !important`, width: isNew ? '250px' : '230px' }}>
                    {isNew && (
                        <Box
                            sx={{
                                '&&': {
                                    p: 1.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: `${dropdownColors.searchBackground} !important`,
                                    boxShadow: '0px 3px 8px rgba(0,0,0,0.15)',
                                    position: 'relative',
                                    zIndex: 1,
                                },
                            }}
                        >
                            <SearchIcon sx={{ color: dropdownColors.searchIcon }} />

                            <InputBase
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search Sector"
                                sx={{
                                    width: '100%',
                                    '&&': { fontSize: 14 },
                                    '&& input': {
                                        color: dropdownColors.searchText,
                                        caretColor: dropdownColors.searchText,
                                        fontSize: 14,
                                    },
                                    '&& input::placeholder': {
                                        color: dropdownColors.searchPlaceholder,
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    )}
                    <Stack
                        sx={{
                            height: '140px',
                            backgroundColor: isNew ? `${dropdownColors.panelBackground} !important` : undefined,
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': { width: '8px' },
                            '&::-webkit-scrollbar-thumb': { borderRadius: '10px', backgroundColor: `${colors.scrollbar.thumb}` },
                            '&::-webkit-scrollbar-track': { 
                                backgroundColor: 'transparent' 
                            },
                        }}
                    >
                        <MenuItem
                            disableGutters
                            sx={{
                                ...commonMenuItemStyles,
                                ...theme.typography.filterText,
                                backgroundColor: isNew ? `${dropdownColors.panelBackground} !important` : 'dark.dark',
                                borderRadius: isNew ? '0px' : '5px',
                                border: isNew ? 'none' : '1px solid #009BD8',
                                width: '100%'
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="success"
                                        value="all"
                                        checked={itemsChecked}
                                        onChange={selectAllCheck}
                                        disableRipple={isNew}
                                        icon={isNew ? (
                                            <UncheckedIcon
                                                borderColor="#AAAAAA"
                                                backgroundColor={dropdownColors.checkboxBackground}
                                            />
                                        ) : undefined}
                                        checkedIcon={isNew ? (
                                            <CheckedIconBox
                                                borderColor={dropdownColors.checkboxBorder || '#39ACE7'}
                                                checkColor={dropdownColors.checkboxCheck || '#39ACE7'}
                                                backgroundColor={dropdownColors.checkboxBackground}
                                            />
                                        ) : undefined}
                                        sx={isNew ? { '&&': { p: 0 } } : {
                                            '& .MuiSvgIcon-root': {
                                            color: `${colors.dropdown.checkBox} !important`,
                                            },
                                        }}
                                    />
                                }
                                label={isNew ? <Typography sx={{ fontSize: 14, fontWeight: 400, color: dropdownColors.optionText }}>All</Typography> : <Typography variant="filterText">All</Typography>}
                            />
                        </MenuItem>

                        {sectorOptionsList && 
                            (isNew ? visibleSectorOptions : sectorOptionsList)
                                .sort((a, b) => {
                                // Check if 'a' or 'b' is checked (checked items should come first)
                                const isCheckedA = checkedList.includes(a);
                                const isCheckedB = checkedList.includes(b);
                                if (isCheckedA && !isCheckedB) return -1; // 'a' should come before 'b'
                                if (!isCheckedA && isCheckedB) return 1;  // 'b' should come before 'a'
                                return a.localeCompare(b);  // If both are checked or both unchecked, no change in order
                                })
                                .map((filter: any) => (
                                <MenuItem key={filter} disableGutters sx={commonMenuItemStyles}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="success"
                                                value={filter}
                                                checked={checkedList.includes(filter)}
                                                onChange={handleChange}
                                                disableRipple={isNew}
                                                icon={isNew ? (
                                                    <UncheckedIcon
                                                        borderColor="#AAAAAA"
                                                        backgroundColor={dropdownColors.checkboxBackground}
                                                    />
                                                ) : undefined}
                                                checkedIcon={isNew ? (
                                                    <CheckedIconBox
                                                        borderColor={dropdownColors.checkboxBorder || '#39ACE7'}
                                                        checkColor={dropdownColors.checkboxCheck || '#39ACE7'}
                                                        backgroundColor={dropdownColors.checkboxBackground}
                                                    />
                                                ) : undefined}
                                                sx={isNew ? { '&&': { p: 0 } } : {
                                                    '& .MuiSvgIcon-root': {
                                                        color: `${colors.dropdown.checkBox} !important`,
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="filterText">
                                                {filter === true ? 'Yes' : filter === false ? 'No' : filter}
                                            </Typography>
                                        }
                                    />
                                </MenuItem>
                            ))}
                    </Stack>

                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                        <Button variant="pepwiseSecondaryOutline" onClick={handleClose}>
                            Cancel
                        </Button>
						<Button variant="pepwiseSecondaryOutline" onClick={handleOkChange}>
                            OK
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
        </Stack>
    );
};

export default SectorCheckFilter;
